import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Button, Input, Modal } from "@vv/ui";
import { ALL_TEAM_ROLES, TEAM_ROLE_LABELS, TeamRole, type Team } from "@vv/teams-frontend";
import { useState } from "react";

export interface AssignTeamRoleModalProps {
  open: boolean;
  onClose: () => void;
  userLabel: string;
  teams: Team[];
  onConfirm: (teamId: string, role: TeamRole) => Promise<void>;
}

export function AssignTeamRoleModal({
  open,
  onClose,
  userLabel,
  teams,
  onConfirm,
}: AssignTeamRoleModalProps) {
  const [teamId, setTeamId] = useState("");
  const [role, setRole] = useState<TeamRole>(TeamRole.Requestor);
  const [saving, setSaving] = useState(false);

  async function handleConfirm() {
    setSaving(true);
    try {
      await onConfirm(teamId, role);
      setTeamId("");
      setRole(TeamRole.Requestor);
    } catch {
      // Already toasted by the caller - swallow so it doesn't also surface
      // as an unhandled promise rejection.
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Assign to team"
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            loading={saving}
            disabled={teamId === ""}
            onClick={handleConfirm}
          >
            Assign
          </Button>
        </>
      }
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Add <strong>{userLabel}</strong> to a team with a role.
        </Typography>
        <Input
          select
          label="Team"
          value={teamId}
          onChange={(event) => setTeamId(event.target.value)}
          required
          autoFocus
        >
          {teams.length === 0 ? <MenuItem value="">No teams available</MenuItem> : null}
          {teams.map((team) => (
            <MenuItem key={team.id} value={team.id}>
              {team.name}
            </MenuItem>
          ))}
        </Input>
        <Input
          select
          label="Role"
          value={role}
          onChange={(event) => setRole(event.target.value as TeamRole)}
        >
          {ALL_TEAM_ROLES.map((option) => (
            <MenuItem key={option} value={option}>
              {TEAM_ROLE_LABELS[option]}
            </MenuItem>
          ))}
        </Input>
      </Stack>
    </Modal>
  );
}
